"use client";

import * as React from "react";
import { useMediaQuery } from "@ds/hooks/use-media-query";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "./drawer";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import { cn } from "@ds/lib/utils";

/**
 * Sheet — Capture Sheet fields 11.2, 12.11 and 25.10.
 *
 * One API, two presentations. A sheet resolves a SINGLE decision; multi-step
 * commitments get a full-screen flow, browsing goes deeper via navigation. The
 * presentation encodes how consequential the destination is.
 *
 * On touch it is a draggable bottom drawer, which is what the evidence shows.
 * On a wide screen it becomes a centred dialog — a bottom sheet is a touch
 * idiom and reads as broken at desk width. That second half is DESIGNED, not
 * observed: the captured product is phone-only. See capture field 25.10.
 *
 * The breakpoint must equal `--ds-breakpoint-pointer` in tokens/app.css. It is a
 * constant rather than a runtime read of that variable: reading it during render
 * depends on the system scope already being on the document, which is an
 * ordering hazard that fails silently by falling back to a default. The two are
 * kept in step by `npm run lint:systems`, which fails if they diverge.
 */
const POINTER_QUERY = "(min-width: 48rem)";
const PointerCtx = React.createContext(false);

export function useIsPointerLayout() {
  return React.use(PointerCtx);
}

/**
 * The two presentations expose the same parts, but not the same TYPES: a drawer
 * root reports dismissal reasons a dialog root has no word for, and a dialog
 * popup reports a nested-dialog state a drawer popup does not have. Nothing in
 * this file reads either of those, so the pair is addressed through one shape —
 * the dialog's — and the presentation is chosen at runtime. Widening the props
 * instead would make every caller handle a union it can never observe.
 */
const asDialog = <T,>(drawerPart: unknown) => drawerPart as T;

function Sheet(props: React.ComponentProps<typeof Dialog>) {
  const isPointer = useMediaQuery(POINTER_QUERY);
  const Root = isPointer ? Dialog : asDialog<typeof Dialog>(Drawer);
  return (
    <PointerCtx value={isPointer}>
      <Root {...props} />
    </PointerCtx>
  );
}

function SheetTrigger(props: React.ComponentProps<typeof DialogTrigger>) {
  const Comp = useIsPointerLayout() ? DialogTrigger : asDialog<typeof DialogTrigger>(DrawerTrigger);
  return <Comp {...props} />;
}

function SheetClose(props: React.ComponentProps<typeof DialogClose>) {
  const Comp = useIsPointerLayout() ? DialogClose : asDialog<typeof DialogClose>(DrawerClose);
  return <Comp {...props} />;
}

function SheetContent(props: React.ComponentProps<typeof DialogContent>) {
  const Comp = useIsPointerLayout() ? DialogContent : asDialog<typeof DialogContent>(DrawerContent);
  return <Comp {...props} />;
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof DialogTitle>) {
  const Comp = useIsPointerLayout() ? DialogTitle : asDialog<typeof DialogTitle>(DrawerTitle);
  return <Comp className={cn("text-section", className)} {...props} />;
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogDescription>) {
  const Comp = useIsPointerLayout() ? DialogDescription : asDialog<typeof DialogDescription>(DrawerDescription);
  return <Comp className={cn("text-support text-muted-foreground", className)} {...props} />;
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("mb-5 flex flex-col gap-1 pr-10 text-center", className)} {...props} />;
}

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetDescription };
